/*
 * Round 14: settings the SERVER defines that this bundle's generated FIELD_METADATA does not.
 *
 * The settings page works in field-name space through FIELD_METADATA (name -> { number, ... }), a
 * generated copy bundled at build time. A setting the server adds later (round 14: the welcome DM
 * per-hour/per-day limits) is in GET /uiapi/settings/metadata - generated from V1's own page - but
 * not in that copy, so the page could never draw it and never send it. ("Pick targets in order
 * (one per session)" and "auto-remove not-found targets" were slot settings 126 / 127 here until
 * they moved to the Target Data page - lib/targetData.js; the server no longer lists them.)
 *
 * This takes everything about such a setting from the server's metadata: its id, its field name,
 * its label, its section and its control/options. Nothing is invented here (memory:
 * v3-must-not-invent-what-v1-defines). A local entry always wins - a server id this bundle
 * already knows is left exactly as it is.
 *
 * Excluded: the bulk-copy engine's own plumbing, #5001 bulkUpdateSettings and #5002
 * bulkSettingsUpdateData - the only two server ids missing locally before round 14 (measured
 * against settings-ui-metadata.json on 2026-09-28). Neither is a customer setting.
 */

const EXCLUDED_FIELD = /^bulk/i;

/*
 * One server value into what the form controls expect (a Switch wants a boolean, inputs a string).
 *
 * UI-9 (shared phone driver audit F041 / F042, 2026-10-17): a value becomes a boolean ONLY when it is one, when it is
 * a switch's stored text 'true' / 'false', or when it is empty ('' / 'null') for a DECLARED boolean (meta.type
 * 'boolean' - #128 from the server's metadata). `def` alone is a name heuristic (startsWith('dont'), startsWith('auto'))
 * and is wrong for #630 (String, textarea) and #1277 (Integer): the branch's "def is boolean -> boolean" showed them
 * blank / "0" and saved the word 'false' into templates. Everything else is exactly the pre-branch value (String(raw)).
 */
export function coerceForForm(raw, def, meta) {
  const declaredBoolean = !!meta && meta.type === 'boolean';
  const booleanField = declaredBoolean || typeof def === 'boolean';
  if (raw === null || raw === undefined) return booleanField ? false : '';
  if (typeof raw === 'boolean') return raw;
  if (booleanField && (raw === 'true' || raw === 'false')) return raw === 'true';
  if (declaredBoolean && (raw === '' || raw === 'null')) return false;
  return String(raw);
}

// Sections the page knows how to place (fieldMetadataComplete SECTION_ORDER / MAIN_CATEGORIES).
// A server section outside this list goes to "Other Settings" so the tabbed view still shows it.
// Round 15 (2026-09-30): "Close Friends" is the server's own section of 716-722 (settings-ui-metadata.json,
// its own card on V1's page, right before "Instagram Live Commenting"); listed here so those settings get
// their own section in every view instead of landing in "Other Settings". 697-700 (repost from the saved
// collection) are in "Reposting/Post Scheduling Settings", a section this list already has.
// r16 (2026-10-03): "TikTok Settings" (lib/platform.js TIKTOK_SECTION) - the section the TikTok-only settings (ids 2001+,
// design section 2.2) will come in from the server; known here so they get their own section, not "Other Settings".
// Setting 15 "Platform" itself is in "Device Info".
// r16p (2026-10-24, DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 6.10 / 7.3): "DM Inbox (Chats)" - the server's own section of 771-787
// (its V1 card sits after "Outbound DMs Sending"); known here so those settings get their own section, not "Other Settings".
export const KNOWN_SECTIONS = new Set([
  'Device Info',
  'TikTok Settings',
  'App Run Timer Settings',
  'Target Account Filter Settings',
  'Like Limits Settings',
  'Comment Limits Settings',
  'Story Engagements Settings (Hijacks Comments Module)',
  'Follow Limits Settings',
  'Unfollow Limits Settings',
  'Reposting/Post Scheduling Settings',
  'Welcome DM Settings',
  'Close Friends',
  'Instagram Live Commenting',
  'Outbound DMs Sending (Follow Module Extension)',
  'DM Inbox (Chats)',
  'Other Useful Settings',
  'Post/Comment Boosting Settings',
  'Advanced Human Emulation Settings',
  'Additional/Misc Settings',
  'Other Settings',
]);

const serverList = (serverMeta) => {
  const list = serverMeta && (serverMeta.settings || serverMeta);
  return Array.isArray(list) ? list : [];
};

/*
 * r16p (design 6.9 / 6.10, O1): #772 "dmReplyMode" offers 0 read only, 1 manual replies, 2 manual + AI suggestions. Value 3
 * (automatic AI replies that message real people with no human) is NOT built in r16p: the server hides it from the metadata and
 * refuses it by value on every write path. This bundle drops it too, even if a server ever lists it, so a select can never offer it.
 */
export const DM_REPLY_MODE_ID = 772;
export const DM_REPLY_MODE_REFUSED = new Set(['3']);

/** The options of a server select, minus the values r16p refuses (only #772 = 3 today). Pure. */
export function allowedServerOptions(id, options) {
  if (!Array.isArray(options)) return options;
  if (Number(id) !== DM_REPLY_MODE_ID) return options;
  return options.filter((o) => {
    const v = o && typeof o === 'object' ? (o.value !== undefined ? o.value : o.id) : o;
    return !DM_REPLY_MODE_REFUSED.has(String(v).trim());
  });
}

/** One server metadata entry -> a FIELD_METADATA-shaped entry. */
export function metaFromServer(s) {
  const section = KNOWN_SECTIONS.has(s.section) ? s.section : 'Other Settings';
  const meta = {
    number: s.id,
    label: s.label || s.javaField,
    section,
    fromServer: true,
  };
  const control = String(s.control || '').toLowerCase();
  const jt = String(s.javaType || '').toLowerCase();
  if (control === 'checkbox' || jt === 'boolean') {
    meta.type = 'boolean';
  } else if (control === 'select' && Array.isArray(s.options) && s.options.length) {
    meta.type = 'select';
    meta.options = allowedServerOptions(s.id, s.options);   // { value, text } - lib/settingsOptions reads both shapes
  } else if (['integer', 'int', 'long', 'double', 'float'].includes(jt) || control === 'number') {
    meta.type = 'number';
  }
  if (s.min !== null && s.min !== undefined) meta.min = s.min;
  if (s.max !== null && s.max !== undefined) meta.max = s.max;
  // r16h (2026-10-12, setting 723 "Repost Caption Template"): optional plain-text help and placeholder, when the server
  // sends them. The page draws `help` as TEXT under the box - never as HTML, so a token such as <sourceusername> shows
  // as typed instead of being swallowed as a tag (the tooltip path renders HTML).
  if (typeof s.help === 'string' && s.help.trim() !== '') meta.help = s.help;
  if (typeof s.placeholder === 'string' && s.placeholder !== '') meta.placeholder = s.placeholder;
  return meta;
}

/**
 * The server settings this bundle does not know, as FIELD_METADATA entries keyed by field name.
 * Pure - exported for tests.
 */
export function serverOnlyFields(serverMeta, localMeta) {
  const local = localMeta || {};
  const localNumbers = new Set();
  Object.values(local).forEach((m) => {
    if (m && m.number !== undefined && m.number !== null) localNumbers.add(Number(m.number));
  });
  const out = {};
  serverList(serverMeta).forEach((s) => {
    if (!s || s.id === undefined || s.id === null || !s.javaField) return;
    if (localNumbers.has(Number(s.id))) return;          // the bundle already has this id
    if (EXCLUDED_FIELD.test(s.javaField)) return;        // #5001 / #5002
    if (Object.prototype.hasOwnProperty.call(local, s.javaField)) return; // never shadow a local name
    out[s.javaField] = metaFromServer(s);
  });
  return out;
}

/**
 * Adds the server-only settings to FIELD_METADATA in place (the page, the diff, templates and the
 * search all read that one object). Idempotent. Returns the names added by THIS call.
 */
export function registerServerOnlyFields(serverMeta, fieldMetadata) {
  const extra = serverOnlyFields(serverMeta, fieldMetadata);
  const added = Object.keys(extra);
  added.forEach((name) => { fieldMetadata[name] = extra[name]; });
  if (added.length) {
    // eslint-disable-next-line no-console
    console.info(`[R14-UI] settings: ${added.length} server-only setting(s) added from /settings/metadata: `
      + added.map((n) => `#${extra[n].number} ${n}`).join(', '));
  }
  return added;
}
